import { Injectable, inject, Injector, runInInjectionContext } from '@angular/core';
import {
  Firestore,
  collection,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  collectionData,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  Timestamp,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Observable, map, of } from 'rxjs';
import { AuthService } from './auth.service';
import { StorageService } from './storage.service';
import {
  FactoringSchedule,
  FactoringScheduleLineItem,
} from '../models/factoring-schedule.model';
import { Invoice, InvoiceDocument } from '../models/invoice.model';
import { LoadView } from '../models/load.model';

@Injectable({
  providedIn: 'root',
})
export class FactoringScheduleService {
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);
  private readonly authService = inject(AuthService);
  private readonly storageService = inject(StorageService);
  private readonly injector = inject(Injector);

  private sanitizeForFirestore<T>(val: T): T {
    if (val === null || val === undefined || typeof val !== 'object') {
      return val;
    }
    if (Array.isArray(val)) {
      return val
        .filter((item) => item !== undefined)
        .map((item) => this.sanitizeForFirestore(item)) as unknown as T;
    }
    const proto = Object.getPrototypeOf(val);
    if (proto && proto !== Object.prototype) {
      return val;
    }
    const cleaned: Record<string, any> = {};
    for (const [k, v] of Object.entries(val as Record<string, any>)) {
      if (v !== undefined) {
        cleaned[k] = this.sanitizeForFirestore(v);
      }
    }
    return cleaned as T;
  }

  private generateShortInvoiceNumber(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = 'INV-';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  /**
   * Live stream of all Payout / Factoring Schedules for the active company.
   */
  getSchedules(): Observable<FactoringSchedule[]> {
    const companyId = this.authService.activeCompanyId();
    if (!companyId) return of([]);

    const ref = collection(this.firestore, 'factoringSchedules');
    const q = query(ref, where('ownerCompanyId', '==', companyId));

    return runInInjectionContext(
      this.injector,
      () => collectionData(q, { idField: 'id' }) as Observable<FactoringSchedule[]>
    ).pipe(
      map((schedules) =>
        schedules.sort((a, b) => {
          const timeA = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const timeB = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return timeB - timeA;
        })
      )
    );
  }

  /**
   * Uploads one or more schedule/remittance PDFs to Firebase Storage, immediately creates
   * non-blocking `PROCESSING` containers in `factoringSchedules`, and triggers the
   * `processFactoringSchedule` Cloud Function in the background for each file.
   */
  async uploadAndProcessSchedules(
    files: File[],
    options?: { hintInvoiceId?: string | null }
  ): Promise<string[]> {
    const user = this.authService.user();
    const appUser = this.authService.appUser();
    const companyId = this.authService.activeCompanyId();
    if (!user || !companyId) {
      throw new Error('User or active company not authenticated');
    }

    const uploadedByName =
      appUser?.firstName && appUser?.lastName
        ? `${appUser.firstName} ${appUser.lastName}`
        : user.displayName || user.email || 'Unknown User';

    const createdIds: string[] = [];

    for (const file of files) {
      const scheduleRef = doc(collection(this.firestore, 'factoringSchedules'));
      const scheduleId = scheduleRef.id;
      const storagePath = `factoringSchedules/${companyId}/${scheduleId}`;

      const upload$ = this.storageService.uploadFile(file, storagePath);
      const { downloadUrl } = await new Promise<{ downloadUrl: string }>(
        (resolve, reject) => {
          upload$.subscribe({
            next: (state) => {
              if (state.downloadUrl) {
                resolve({ downloadUrl: state.downloadUrl });
              }
            },
            error: (err) => reject(err),
          });
        }
      );

      const initialDoc: Omit<FactoringSchedule, 'id'> = {
        ownerCompanyId: companyId,
        status: 'PROCESSING',
        scheduleType: 'factoring',
        fileName: file.name,
        fileUrl: downloadUrl,
        fileSize: file.size,
        totalGrossAmount: 0,
        totalFeeAmount: 0,
        reserveEscrowDeducted: 0,
        totalFuelAdvanceAmount: 0,
        expensesDeducted: 0,
        netPaidToClient: 0,
        lineItems: [],
        hintInvoiceId: options?.hintInvoiceId || null,
        linkedInvoiceIds: options?.hintInvoiceId ? [options.hintInvoiceId] : [],
        linkedLoadIds: [],
        uploadedBy: user.uid,
        uploadedByName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await setDoc(scheduleRef, initialDoc);
      createdIds.push(scheduleId);

      // Fire-and-forget the AI Cloud Function so the UI stays non-blocking
      const callable = httpsCallable(this.functions, 'processFactoringSchedule');
      callable({
        scheduleId,
        companyId,
        pdfUrl: downloadUrl,
        fileName: file.name,
        hintInvoiceId: options?.hintInvoiceId || null,
      }).catch((err) => {
        console.error(
          `[FactoringScheduleService] Background AI parse failed for ${scheduleId}:`,
          err
        );
      });
    }

    return createdIds;
  }

  /**
   * Retries AI extraction for a failed schedule container.
   */
  async retrySchedule(schedule: FactoringSchedule): Promise<void> {
    const companyId = this.authService.activeCompanyId();
    if (!companyId) return;

    const scheduleRef = doc(this.firestore, `factoringSchedules/${schedule.id}`);
    await updateDoc(scheduleRef, {
      status: 'PROCESSING',
      errorMessage: null,
      updatedAt: serverTimestamp(),
    });

    const callable = httpsCallable(this.functions, 'processFactoringSchedule');
    callable({
      scheduleId: schedule.id,
      companyId,
      pdfUrl: schedule.fileUrl,
      fileName: schedule.fileName,
      hintInvoiceId: schedule.hintInvoiceId || null,
    }).catch((err) => {
      console.error(
        `[FactoringScheduleService] Retry failed for ${schedule.id}:`,
        err
      );
    });
  }

  /**
   * Deletes an unapplied schedule container (`PROCESSING`, `READY_FOR_REVIEW`, or `FAILED`).
   */
  async deleteSchedule(scheduleId: string): Promise<void> {
    const scheduleRef = doc(this.firestore, `factoringSchedules/${scheduleId}`);
    await deleteDoc(scheduleRef);
  }

  /**
   * Applies the human-verified Payout / Factoring Schedule to all selected load views
   * (`loads/{loadId}/views/{companyId}`) and reconciles all affected invoices (Many-to-Many).
   *
   * When `options.backfillMode` is true (or `options.autoCreateInvoiceForUninvoicedLoads` is true),
   * automatically creates a completed Invoice record for loads that are not yet on a valid invoice
   * (or overwrites dummy unpaid invoices in backfill mode).
   */
  async applySchedule(
    schedule: FactoringSchedule,
    verifiedLineItems: FactoringScheduleLineItem[],
    options?: {
      backfillMode?: boolean;
      autoCreateInvoiceForUninvoicedLoads?: boolean;
    }
  ): Promise<{
    updatedLoadsCount: number;
    updatedInvoicesCount: number;
    createdInvoiceId?: string;
  }> {
    const user = this.authService.user();
    const appUser = this.authService.appUser();
    const companyId = this.authService.activeCompanyId();
    if (!user || !companyId) {
      throw new Error('User or active company not authenticated');
    }

    const selectedItems = verifiedLineItems.filter(
      (item) => item.selected !== false && !!item.matchedLoadId
    );
    if (selectedItems.length === 0) {
      throw new Error('Please select at least one matched load to apply.');
    }

    const fundingDateObj = schedule.fundingDate
      ? new Date(`${schedule.fundingDate}T12:00:00Z`)
      : new Date();
    const fundingTimestamp = !isNaN(fundingDateObj.getTime())
      ? Timestamp.fromDate(fundingDateObj)
      : Timestamp.now();

    const uploadedByName =
      appUser?.firstName && appUser?.lastName
        ? `${appUser.firstName} ${appUser.lastName}`
        : user.displayName || user.email || 'Unknown User';

    const effectiveScheduleId = schedule.replacesScheduleId || schedule.id;

    const scheduleInvoiceDoc: InvoiceDocument = {
      id: `sched_${effectiveScheduleId}`,
      type:
        schedule.scheduleType === 'direct_remittance'
          ? 'payment_proof'
          : 'factoring_schedule',
      title: schedule.scheduleNumber
        ? `Schedule #${schedule.scheduleNumber}`
        : schedule.fileName,
      fileName: schedule.fileName,
      fileUrl: schedule.fileUrl,
      ...(schedule.fileSize !== undefined ? { fileSize: schedule.fileSize } : {}),
      uploadedAt: Timestamp.now(),
      uploadedBy: user.uid,
      uploadedByName,
    };

    const batch = writeBatch(this.firestore);
    const affectedInvoiceIds = new Set<string>();
    if (schedule.hintInvoiceId) {
      affectedInvoiceIds.add(schedule.hintInvoiceId);
    }

    const expectedInvNumber = schedule.scheduleNumber
      ? `INV-${schedule.scheduleNumber.trim()}`.toUpperCase()
      : null;

    // Look up whether an existing Invoice already exists for THIS schedule (e.g. re-upload or editing an applied schedule).
    // IMPORTANT: Never match an invoice belonging to a DIFFERENT schedule number (which happens when a load is split across two schedules).
    let existingScheduleInvoice: (Invoice & { id: string }) | null = null;
    const candidateExistingInvId =
      schedule.targetExistingInvoiceId ||
      schedule.hintInvoiceId ||
      (schedule.status === 'APPLIED' && schedule.linkedInvoiceIds?.length === 1
        ? schedule.linkedInvoiceIds[0]
        : null);

    if (candidateExistingInvId) {
      const snap = await getDoc(
        doc(this.firestore, `invoices/${candidateExistingInvId}`)
      );
      if (snap.exists()) {
        const invData = snap.data() as Invoice;
        const invNumUpper = (invData.invoiceNumber || '').trim().toUpperCase();
        const belongsToThisSchedule =
          !expectedInvNumber ||
          invNumUpper === expectedInvNumber ||
          invData.factoringScheduleIds?.includes(effectiveScheduleId) ||
          candidateExistingInvId === schedule.hintInvoiceId;
        if (
          invData.ownerCompanyId === companyId &&
          invData.status !== 'voided' &&
          invData.status !== 'VOIDED' &&
          belongsToThisSchedule
        ) {
          existingScheduleInvoice = { ...invData, id: snap.id };
        }
      }
    }

    if (!existingScheduleInvoice && expectedInvNumber) {
      const invQ = query(
        collection(this.firestore, 'invoices'),
        where('ownerCompanyId', '==', companyId)
      );
      const invSnaps = await getDocs(invQ);
      for (const d of invSnaps.docs) {
        const invData = d.data() as Invoice;
        if (invData.status === 'voided' || invData.status === 'VOIDED') continue;
        if (
          (invData.invoiceNumber || '').trim().toUpperCase() === expectedInvNumber ||
          invData.factoringScheduleIds?.includes(effectiveScheduleId)
        ) {
          existingScheduleInvoice = { ...invData, id: d.id };
          break;
        }
      }
    }

    const selectedLoadIdsSet = new Set<string>(
      selectedItems.map((i) => i.matchedLoadId!).filter(Boolean)
    );
    const dismissedCancelledDuplicateIds = new Set<string>();
    for (const item of selectedItems) {
      if (
        (item.candidateLoads?.length || 0) > 1 &&
        item.dismissUnselectedCancelledDuplicates !== false
      ) {
        for (const cand of item.candidateLoads!) {
          if (
            cand.loadId !== item.matchedLoadId &&
            cand.loadStatus === 'CANCELLED' &&
            !selectedLoadIdsSet.has(cand.loadId)
          ) {
            dismissedCancelledDuplicateIds.add(cand.loadId);
          }
        }
      }
    }

    // Check any `matchedInvoiceId` values on selected items to distinguish between:
    // - An unpaid pre-schedule invoice (which can be reconciled or superseded in backfillMode)
    // - Another schedule's invoice (e.g. `INV-DPTX-1197` when applying `DPTX-1200` for a split load like `AMR13399757A`),
    //   which must NEVER be voided or mutated by this schedule!
    const otherScheduleInvoiceIds = new Set<string>();
    const candidateMatchedInvIds = Array.from(
      new Set(
        selectedItems
          .map((i) => i.matchedInvoiceId)
          .filter((id): id is string => !!id && id !== existingScheduleInvoice?.id)
      )
    );
    for (const mInvId of candidateMatchedInvIds) {
      const mSnap = await getDoc(doc(this.firestore, `invoices/${mInvId}`));
      if (mSnap.exists()) {
        const mData = mSnap.data() as Invoice;
        const mNumUpper = (mData.invoiceNumber || '').trim().toUpperCase();
        const isOtherScheduleInvoice =
          Boolean(mData.hasFactoringSchedule) ||
          (mData.factoringScheduleIds?.length || 0) > 0 ||
          (expectedInvNumber && mNumUpper.startsWith('INV-') && mNumUpper !== expectedInvNumber);
        if (isOtherScheduleInvoice) {
          otherScheduleInvoiceIds.add(mInvId);
        }
      }
    }

    const uninvoicedItems: FactoringScheduleLineItem[] = [];

    for (const item of selectedItems) {
      const mInvId = item.matchedInvoiceId;
      // If this schedule has its own scheduleNumber (or if the load's matchedInvoiceId belongs to another schedule),
      // include this line item in THIS schedule's invoice so split payments across two schedules each stay on their own schedule's invoice.
      if (
        mInvId &&
        !options?.backfillMode &&
        mInvId !== existingScheduleInvoice?.id &&
        !otherScheduleInvoiceIds.has(mInvId) &&
        !expectedInvNumber
      ) {
        affectedInvoiceIds.add(mInvId);
      } else {
        uninvoicedItems.push(item);
      }
    }

    let createdInvoiceId: string | undefined;
    let createdInvoiceNumber: string | undefined;

    if (existingScheduleInvoice && (uninvoicedItems.length > 0 || options?.backfillMode)) {
      // Upsert / merge newly matched loads into the existing Invoice for this schedule instead of creating a duplicate!
      createdInvoiceId = existingScheduleInvoice.id;
      createdInvoiceNumber = existingScheduleInvoice.invoiceNumber;
      const existingInvRef = doc(this.firestore, `invoices/${existingScheduleInvoice.id}`);

      const itemsToMerge = options?.backfillMode ? selectedItems : uninvoicedItems;
      const baseExistingLoadIds = (existingScheduleInvoice.loadIds || []).filter(
        (id) => !dismissedCancelledDuplicateIds.has(id)
      );
      const mergedLoadIds = Array.from(
        new Set([
          ...baseExistingLoadIds,
          ...itemsToMerge.map((i) => i.matchedLoadId!),
        ])
      );

      const snapshotMap = new Map<string, any>();
      for (const s of existingScheduleInvoice.loadSnapshots || []) {
        if (!dismissedCancelledDuplicateIds.has(s.loadId)) {
          snapshotMap.set(s.loadId, s);
        }
      }
      for (const i of itemsToMerge) {
        const isCancelledTonu = i.matchedLoadStatus === 'CANCELLED';
        const prevSnap = snapshotMap.get(i.matchedLoadId!);
        snapshotMap.set(i.matchedLoadId!, {
          loadId: i.matchedLoadId!,
          externalId: i.matchedExternalId || i.rawLoadRef,
          rawLoadRef: i.rawLoadRef,
          counterpartyName:
            i.debtorName ||
            prevSnap?.counterpartyName ||
            'Broker',
          paymentStatus: i.proposedPaymentStatus === 'paid' ? 'paid' : 'partially_paid',
          paidAmount: i.fundedAmount,
          isSplitSchedulePayment: Boolean(i.isSplitSchedulePayment),
          pricing: {
            ...(prevSnap?.pricing || {}),
            baseRate: isCancelledTonu ? 0 : i.grossAmount,
            tonu: isCancelledTonu ? i.grossAmount : 0,
            lumper: prevSnap?.pricing?.lumper || 0,
            fuelSurcharge: prevSnap?.pricing?.fuelSurcharge || 0,
            otherCharges: prevSnap?.pricing?.otherCharges || 0,
            deductions: prevSnap?.pricing?.deductions || 0,
            total:
              prevSnap?.pricing?.total && Math.abs(prevSnap.pricing.total - i.grossAmount) < 0.01
                ? prevSnap.pricing.total
                : i.grossAmount,
          },
        });
      }
      const mergedSnapshots = Array.from(snapshotMap.values());
      const mergedGrossSum = Number(
        mergedSnapshots
          .reduce((sum, s) => sum + (Number(s.pricing?.total) || 0), 0)
          .toFixed(2)
      );

      const allScheduleMatchedItems = verifiedLineItems.filter(
        (i) => !!i.matchedLoadId && (i.selected || i.matchStatus === 'ALREADY_PAID')
      );
      const feeSum = Number(
        (
          schedule.totalFeeAmount ||
          allScheduleMatchedItems.reduce((s, i) => s + (Number(i.feeAmount) || 0), 0)
        ).toFixed(2)
      );
      const reserveSum = Number(
        (
          schedule.reserveEscrowDeducted ||
          allScheduleMatchedItems.reduce((s, i) => s + (Number(i.reserveAmount) || 0), 0)
        ).toFixed(2)
      );
      const fundedSum = Number(
        allScheduleMatchedItems
          .reduce((s, i) => s + (Number(i.fundedAmount) || 0), 0)
          .toFixed(2)
      );
      const txFee = Number(schedule.expensesDeducted || 0);
      const netFromLoadsAfterTxFee = Math.max(
        0,
        Number((fundedSum - txFee).toFixed(2))
      );
      const netReceived =
        schedule.netPaidToClient > 0
          ? schedule.netPaidToClient
          : netFromLoadsAfterTxFee > 0
            ? netFromLoadsAfterTxFee
            : Math.max(Number(existingScheduleInvoice.receivedAmount || 0), 0);
      const explicitFees = Number((feeSum + reserveSum + txFee).toFixed(2));
      const totalDeductionsAndFees =
        explicitFees > 0
          ? explicitFees
          : Math.max(0, Number((mergedGrossSum - netReceived).toFixed(2)));

      const existingDocs = existingScheduleInvoice.documents || [];
      const alreadyHasScheduleDoc = existingDocs.some(
        (d) =>
          d.fileUrl === scheduleInvoiceDoc.fileUrl ||
          d.id === scheduleInvoiceDoc.id
      );

      batch.update(existingInvRef, this.sanitizeForFirestore({
        status: 'paid',
        paidAt: fundingTimestamp,
        loadIds: mergedLoadIds,
        loadSnapshots: mergedSnapshots,
        totalAmount: mergedGrossSum,
        receivedAmount: netReceived,
        feeAmount: totalDeductionsAndFees,
        factoringFees: {
          baseFee: feeSum,
          reserveAmount: reserveSum,
          transactionFee: schedule.expensesDeducted || 0,
          basePercentage:
            mergedGrossSum > 0
              ? Number(((feeSum / mergedGrossSum) * 100).toFixed(2))
              : 0,
          reservePercentage:
            mergedGrossSum > 0
              ? Number(((reserveSum / mergedGrossSum) * 100).toFixed(2))
              : 0,
        },
        ...(alreadyHasScheduleDoc
          ? {}
          : { documents: arrayUnion(scheduleInvoiceDoc) }),
        hasFactoringSchedule: true,
        factoringScheduleIds: arrayUnion(effectiveScheduleId),
        scheduleExpensesDeducted: schedule.expensesDeducted || 0,
        updatedAt: serverTimestamp(),
      }));
      affectedInvoiceIds.add(existingScheduleInvoice.id);
    } else {
      // In backfillMode (or when autoCreateInvoiceForUninvoicedLoads is enabled and loads have no invoice),
      // create an Invoice record representing this schedule's uninvoiced loads (or all loads in backfillMode).
      const shouldCreateInvoice =
        (options?.backfillMode && selectedItems.length > 0) ||
        (options?.autoCreateInvoiceForUninvoicedLoads &&
          uninvoicedItems.length > 0 &&
          !schedule.hintInvoiceId);

      if (shouldCreateInvoice) {
        const itemsForNewInvoice = options?.backfillMode
          ? selectedItems
          : uninvoicedItems;

        // If backfillMode is overwriting existing dummy unpaid invoices, void them first —
        // but NEVER void another schedule's invoice (e.g. when a load is paid across two separate schedules)!
        if (options?.backfillMode) {
          const oldInvoiceIds = new Set<string>();
          for (const it of selectedItems) {
            if (it.matchedInvoiceId && !otherScheduleInvoiceIds.has(it.matchedInvoiceId)) {
              oldInvoiceIds.add(it.matchedInvoiceId);
            }
          }
          for (const oldInvId of oldInvoiceIds) {
            affectedInvoiceIds.delete(oldInvId);
            const oldInvRef = doc(this.firestore, `invoices/${oldInvId}`);
            batch.update(oldInvRef, {
              status: 'voided',
              voidedAt: serverTimestamp(),
              voidedBy: user.uid,
              voidReason: `Superseded by historical schedule backfill (${schedule.scheduleNumber || schedule.fileName})`,
              updatedAt: serverTimestamp(),
            });
          }
        }

        const newInvRef = doc(collection(this.firestore, 'invoices'));
        createdInvoiceId = newInvRef.id;
        createdInvoiceNumber = schedule.scheduleNumber
          ? `INV-${schedule.scheduleNumber}`
          : this.generateShortInvoiceNumber();

        const grossSum = Number(
          itemsForNewInvoice
            .reduce((s, i) => s + (Number(i.grossAmount) || 0), 0)
            .toFixed(2)
        );
        const feeSum = Number(
          itemsForNewInvoice
            .reduce((s, i) => s + (Number(i.feeAmount) || 0), 0)
            .toFixed(2)
        );
        const reserveSum = Number(
          (
            schedule.reserveEscrowDeducted ||
            itemsForNewInvoice.reduce(
              (s, i) => s + (Number(i.reserveAmount) || 0),
              0
            )
          ).toFixed(2)
        );
        const fundedSum = Number(
          itemsForNewInvoice
            .reduce((s, i) => s + (Number(i.fundedAmount) || 0), 0)
            .toFixed(2)
        );
        const txFee = Number(schedule.expensesDeducted || 0);
        const netReceived =
          itemsForNewInvoice.length === selectedItems.length &&
          schedule.netPaidToClient > 0
            ? schedule.netPaidToClient
            : Number((fundedSum - txFee).toFixed(2));

        const explicitFees = Number((feeSum + reserveSum + txFee).toFixed(2));
        const totalDeductionsAndFees =
          explicitFees > 0
            ? explicitFees
            : Math.max(0, Number((grossSum - netReceived).toFixed(2)));

        const newInvoiceDoc: Omit<Invoice, 'id'> = {
          ownerCompanyId: companyId,
          invoiceNumber: createdInvoiceNumber,
          status: 'paid',
          invoicingMethod:
            schedule.scheduleType === 'direct_remittance'
              ? 'direct'
              : 'factoring',
          billedToId: schedule.factoringCompanyId || 'factoring',
          billedToName:
            schedule.factoringCompanyName ||
            itemsForNewInvoice[0]?.debtorName ||
            'Factoring Company',
          loadIds: Array.from(new Set(itemsForNewInvoice.map((i) => i.matchedLoadId!))),
          loadSnapshots: itemsForNewInvoice.map((i) => {
            const isCancelledTonu = i.matchedLoadStatus === 'CANCELLED';
            return {
              loadId: i.matchedLoadId!,
              externalId: i.matchedExternalId || i.rawLoadRef,
              rawLoadRef: i.rawLoadRef,
              counterpartyName: i.debtorName || 'Broker',
              paymentStatus: i.proposedPaymentStatus === 'paid' ? 'paid' : 'partially_paid',
              paidAmount: i.fundedAmount,
              isSplitSchedulePayment: Boolean(i.isSplitSchedulePayment),
              pricing: {
                baseRate: isCancelledTonu ? 0 : i.grossAmount,
                tonu: isCancelledTonu ? i.grossAmount : 0,
                lumper: 0,
                fuelSurcharge: 0,
                otherCharges: 0,
                deductions: 0,
                total: i.grossAmount,
              },
            };
          }),
          totalAmount: grossSum,
          receivedAmount: netReceived,
          feeAmount: Math.max(0, totalDeductionsAndFees),
          factoringFees: {
            baseFee: feeSum,
            reserveAmount: reserveSum,
            transactionFee: schedule.expensesDeducted || 0,
            basePercentage:
              grossSum > 0 ? Number(((feeSum / grossSum) * 100).toFixed(2)) : 0,
            reservePercentage:
              grossSum > 0
                ? Number(((reserveSum / grossSum) * 100).toFixed(2))
                : 0,
          },
          createdBy: user.uid,
          createdByEmail: user.email || '',
          invoicedAt: fundingTimestamp,
          paidAt: fundingTimestamp,
          documents: [scheduleInvoiceDoc],
          hasFactoringSchedule: true,
          factoringScheduleIds: [effectiveScheduleId],
          scheduleExpensesDeducted: schedule.expensesDeducted || 0,
          createdAt: fundingTimestamp,
          updatedAt: serverTimestamp() as any,
        };

        batch.set(newInvRef, this.sanitizeForFirestore(newInvoiceDoc));
        affectedInvoiceIds.add(createdInvoiceId);
      }
    }

    // 1. Update each selected load's company view (`loads/{loadId}/views/{companyId}`),
    // preserving and accumulating per-schedule settlements when a load is split across multiple schedules.
    for (const item of selectedItems) {
      const loadId = item.matchedLoadId!;
      const viewRef = doc(this.firestore, `loads/${loadId}/views/${companyId}`);
      const existingViewSnap = await getDoc(viewRef);
      const existingView = existingViewSnap.exists()
        ? (existingViewSnap.data() as any)
        : null;

      const targetInvoiceId =
        createdInvoiceId ||
        (item.matchedInvoiceId && !otherScheduleInvoiceIds.has(item.matchedInvoiceId)
          ? item.matchedInvoiceId
          : null) ||
        schedule.hintInvoiceId ||
        existingView?.invoiceId ||
        null;
      const targetInvoiceNumber =
        createdInvoiceNumber ||
        (item.matchedInvoiceNumber &&
        (!item.matchedInvoiceId || !otherScheduleInvoiceIds.has(item.matchedInvoiceId))
          ? item.matchedInvoiceNumber
          : null) ||
        existingView?.invoiceNumber ||
        null;

      // Build cumulative per-schedule settlement map so multiple schedules (e.g. DPTX-1197 base rate + DPTX-1200 lumper)
      // never overwrite each other's settlement history on the load view.
      const settlementsBySchedule: Record<string, any> = {
        ...(existingView?.factoringSettlementsBySchedule || {}),
      };
      if (
        existingView?.factoringSettlement?.scheduleId &&
        !settlementsBySchedule[existingView.factoringSettlement.scheduleId]
      ) {
        settlementsBySchedule[existingView.factoringSettlement.scheduleId] = {
          ...existingView.factoringSettlement,
          invoiceId: existingView.invoiceId || null,
          invoiceNumber: existingView.invoiceNumber || null,
        };
      }

      const currentSettlementEntry = {
        scheduleId: effectiveScheduleId,
        scheduleNumber: schedule.scheduleNumber || schedule.batchId || null,
        invoiceId: targetInvoiceId,
        invoiceNumber: targetInvoiceNumber,
        rawLoadRef: item.rawLoadRef,
        grossAmount: item.grossAmount,
        feeAmount: item.feeAmount,
        reserveHeld: item.reserveAmount,
        fuelAdvance: item.fuelAdvanceAmount || 0,
        fundedAmount: item.fundedAmount,
        fundedAt: fundingTimestamp,
      };
      settlementsBySchedule[effectiveScheduleId] = currentSettlementEntry;

      // Also deduplicate if an old entry had the same scheduleNumber under a different temporary schedule doc ID
      if (schedule.scheduleNumber) {
        const normSchedNum = schedule.scheduleNumber.trim().toUpperCase();
        for (const [k, val] of Object.entries(settlementsBySchedule)) {
          if (
            k !== effectiveScheduleId &&
            String((val as any)?.scheduleNumber || '').trim().toUpperCase() === normSchedNum
          ) {
            delete settlementsBySchedule[k];
          }
        }
      }

      const allSettlementEntries = Object.values(settlementsBySchedule);
      const cumulativeGrossFactored = Number(
        allSettlementEntries
          .reduce((sum, s: any) => sum + (Number(s?.grossAmount) || 0), 0)
          .toFixed(2)
      );
      const cumulativeFundedPaid = Number(
        allSettlementEntries
          .reduce((sum, s: any) => sum + (Number(s?.fundedAmount) || 0), 0)
          .toFixed(2)
      );

      const expectedLoadGross = Number(
        existingView?.pricing?.totalAmount ??
          existingView?.pricing?.baseRate ??
          existingView?.bookedAtRate ??
          0
      );
      const isCumulativePaid =
        item.proposedPaymentStatus === 'paid' ||
        (expectedLoadGross > 0 &&
          cumulativeGrossFactored >= expectedLoadGross - 0.05 &&
          cumulativeFundedPaid > 0);

      const billingStatus = isCumulativePaid ? 'PAID' : 'PARTIALLY_PAID';
      const pricingStatus = isCumulativePaid ? 'paid' : 'partially_paid';
      const resolvedPaymentStatus = isCumulativePaid ? 'paid' : 'partially_paid';

      const isCancelledTonu = item.matchedLoadStatus === 'CANCELLED';

      // IMPORTANT: Use nested `pricing` object with `batch.set(..., { merge: true })`
      // so Firestore deep-merges into `view.pricing` instead of creating literal dot-keys.
      const pricingMerge: Record<string, any> = {
        status: pricingStatus,
        paymentStatus: resolvedPaymentStatus,
        paidAmount: cumulativeFundedPaid,
      };
      if (isCancelledTonu) {
        pricingMerge['tonu'] = cumulativeGrossFactored;
      }
      if (targetInvoiceId) {
        pricingMerge['invoiceId'] = targetInvoiceId;
      }

      const viewUpdate: Record<string, any> = {
        loadId,
        companyId,
        billingStatus,
        paidAmount: cumulativeFundedPaid,
        paidAt: fundingTimestamp,
        factoringScheduleIds: arrayUnion(effectiveScheduleId),
        factoringSettlement: currentSettlementEntry,
        factoringSettlementsBySchedule: settlementsBySchedule,
        pricing: pricingMerge,
        updatedAt: serverTimestamp(),
      };

      if (isCancelledTonu) {
        viewUpdate['tonuDecision'] = {
          status: 'invoiced',
          amount: cumulativeGrossFactored,
        };
      }
      if (targetInvoiceId) {
        viewUpdate['invoiceId'] = targetInvoiceId;
        viewUpdate['invoiceIds'] = arrayUnion(targetInvoiceId);
      }
      if (targetInvoiceNumber) {
        viewUpdate['invoiceNumber'] = targetInvoiceNumber;
        viewUpdate['invoiceNumbers'] = arrayUnion(targetInvoiceNumber);
      }

      batch.set(viewRef, this.sanitizeForFirestore(viewUpdate), { merge: true });
    }

    // 1b. Dismiss unselected CANCELLED duplicate loads (or clear mistaken Paid status from earlier runs)
    for (const dismissedLoadId of dismissedCancelledDuplicateIds) {
      const dismissedViewRef = doc(
        this.firestore,
        `loads/${dismissedLoadId}/views/${companyId}`
      );
      batch.set(
        dismissedViewRef,
        {
          loadId: dismissedLoadId,
          companyId,
          billingStatus: 'NOT_APPLICABLE',
          paidAmount: 0,
          paidAt: null,
          invoiceId: null,
          invoiceNumber: null,
          factoringSettlement: null,
          pricing: {
            status: 'will_not_invoice',
            paymentStatus: 'unpaid',
            paidAmount: 0,
            invoiceId: null,
          },
          tonuDecision: {
            status: 'not_applicable',
            amount: 0,
            decisionNotes:
              'Resolved via payout schedule reconciliation (duplicate re-booked load paid)',
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    }

    // 2. Mark the schedule document as APPLIED (or upsert into replacesScheduleId if re-uploaded)
    const finalLinkedInvoiceIds = Array.from(affectedInvoiceIds);
    const allMatchedLoadIds = Array.from(
      new Set(
        verifiedLineItems
          .filter((i) => !!i.matchedLoadId && (i.selected || i.matchStatus === 'ALREADY_PAID'))
          .map((i) => i.matchedLoadId!)
      )
    );
    const updatedLineItems = verifiedLineItems.map((i) =>
      i.selected && i.matchedLoadId
        ? {
            ...i,
            matchedInvoiceId: i.matchedInvoiceId || createdInvoiceId || null,
            matchedInvoiceNumber: i.matchedInvoiceNumber || createdInvoiceNumber || null,
            matchStatus: 'ALREADY_PAID' as const,
            selected: false,
          }
        : i
    );

    if (
      schedule.replacesScheduleId &&
      schedule.replacesScheduleId !== schedule.id
    ) {
      const targetSchedRef = doc(
        this.firestore,
        `factoringSchedules/${schedule.replacesScheduleId}`
      );
      batch.set(
        targetSchedRef,
        this.sanitizeForFirestore({
          status: 'APPLIED',
          fileUrl: schedule.fileUrl,
          fileName: schedule.fileName,
          totalGrossAmount: schedule.totalGrossAmount,
          totalFeeAmount: schedule.totalFeeAmount,
          reserveEscrowDeducted: schedule.reserveEscrowDeducted,
          expensesDeducted: schedule.expensesDeducted,
          netPaidToClient: schedule.netPaidToClient,
          lineItems: updatedLineItems,
          linkedInvoiceIds: arrayUnion(...finalLinkedInvoiceIds),
          linkedLoadIds: arrayUnion(...allMatchedLoadIds),
          appliedAt: serverTimestamp(),
          appliedBy: user.uid,
          updatedAt: serverTimestamp(),
        }),
        { merge: true }
      );
      // Remove the temporary duplicate re-upload container
      batch.delete(doc(this.firestore, `factoringSchedules/${schedule.id}`));
    } else {
      const scheduleRef = doc(this.firestore, `factoringSchedules/${schedule.id}`);
      batch.update(scheduleRef, this.sanitizeForFirestore({
        status: 'APPLIED',
        totalGrossAmount: schedule.totalGrossAmount,
        totalFeeAmount: schedule.totalFeeAmount,
        reserveEscrowDeducted: schedule.reserveEscrowDeducted,
        expensesDeducted: schedule.expensesDeducted,
        netPaidToClient: schedule.netPaidToClient,
        lineItems: updatedLineItems,
        linkedInvoiceIds: finalLinkedInvoiceIds,
        linkedLoadIds: allMatchedLoadIds,
        appliedAt: serverTimestamp(),
        appliedBy: user.uid,
        updatedAt: serverTimestamp(),
      }));
    }

    await batch.commit();

    // 3. Reconcile any additional affected pre-schedule invoices (Many-to-Many)
    let updatedInvoicesCount = createdInvoiceId ? 1 : 0;
    for (const invId of finalLinkedInvoiceIds) {
      if (invId === createdInvoiceId) continue;
      await this.reconcileInvoiceFromLoadViews(
        invId,
        companyId,
        { ...schedule, id: effectiveScheduleId },
        scheduleInvoiceDoc,
        fundingTimestamp
      );
      updatedInvoicesCount++;
    }

    return {
      updatedLoadsCount: selectedItems.length,
      updatedInvoicesCount,
      createdInvoiceId,
    };
  }

  /**
   * Recomputes an Invoice's `status` (`paid` vs `partially_paid`), `receivedAmount`,
   * `factoringScheduleIds`, and `documents` by inspecting all of its `loadIds`' live
   * company views (`loads/{loadId}/views/{companyId}`).
   */
  private async reconcileInvoiceFromLoadViews(
    invoiceId: string,
    companyId: string,
    schedule: FactoringSchedule,
    scheduleInvoiceDoc: InvoiceDocument,
    fundingTimestamp: Timestamp
  ): Promise<void> {
    const invRef = doc(this.firestore, `invoices/${invoiceId}`);
    const invSnap = await getDoc(invRef);
    if (!invSnap.exists()) return;

    const inv = invSnap.data() as Invoice;
    if (inv.ownerCompanyId !== companyId) return;

    const loadIds = inv.loadIds || [];
    let paidCount = 0;
    let partialCount = 0;
    let totalFundedAcrossLoads = 0;

    for (const loadId of loadIds) {
      const viewRef = doc(this.firestore, `loads/${loadId}/views/${companyId}`);
      const viewSnap = await getDoc(viewRef);
      if (!viewSnap.exists()) continue;

      const view = viewSnap.data() as LoadView;
      const rawView = view as any;
      const pStatus =
        view.pricing?.paymentStatus ||
        rawView['pricing.paymentStatus'] ||
        (view.billingStatus === 'PAID' ? 'paid' : 'unpaid');
      const paidAmt = Number(
        view.pricing?.paidAmount ??
          rawView['pricing.paidAmount'] ??
          view.paidAmount ??
          rawView.factoringSettlement?.fundedAmount ??
          0
      );

      if (pStatus === 'paid') {
        paidCount++;
      } else if (pStatus === 'partially_paid' || paidAmt > 0) {
        partialCount++;
      }
      totalFundedAcrossLoads += paidAmt;
    }

    const allPaid = loadIds.length > 0 && paidCount === loadIds.length;
    const anyPaidOrPartial = paidCount > 0 || partialCount > 0;

    let nextStatus = inv.status;
    if (allPaid) {
      nextStatus = 'paid';
    } else if (anyPaidOrPartial) {
      nextStatus = 'partially_paid';
    }

    const existingDocs = inv.documents || [];
    const alreadyHasScheduleDoc = existingDocs.some(
      (d) => d.fileUrl === scheduleInvoiceDoc.fileUrl || d.id === scheduleInvoiceDoc.id
    );

    // Individual load `fundedAmount`s (`Advance Amount`) have Base Fee and Reserve deducted,
    // while `transactionFee` (ACH / Wire / Additional fee, e.g. $5.99) is deducted once at the schedule/invoice level.
    const txFee = Number(
      inv.factoringFees?.transactionFee ??
        inv.scheduleExpensesDeducted ??
        schedule.expensesDeducted ??
        0
    );
    const computedReceivedAfterTxFee =
      totalFundedAcrossLoads > 0
        ? Math.max(0, Number((totalFundedAcrossLoads - txFee).toFixed(2)))
        : Number(inv.receivedAmount || 0);

    const updatePayload: Record<string, any> = {
      status: nextStatus,
      receivedAmount: computedReceivedAfterTxFee,
      hasFactoringSchedule: true,
      factoringScheduleIds: arrayUnion(schedule.id),
      updatedAt: serverTimestamp(),
    };

    if (allPaid) {
      updatePayload['paidAt'] = fundingTimestamp;
    }
    if (!alreadyHasScheduleDoc) {
      updatePayload['documents'] = arrayUnion(scheduleInvoiceDoc);
    }

    await updateDoc(invRef, updatePayload);
  }
}
